import { ExpandMore } from "@mui/icons-material";
import {
  Box,
  Checkbox,
  FormControl,
  InputAdornment,
  Typography,
  useTheme,
} from "@mui/material";
import { DateCalendar } from "@mui/x-date-pickers";
import { useProgress } from "context/ProgressProvider";
import { Formik } from "formik";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { LoanService } from "services/LoanService";
import {
  AppFormikSelect,
  AppFormikTextField,
  AppPrimaryButton,
} from "shared/components/AppComponents";
import { ENTITY_TYPE } from "shared/helpers/Constants";
import {
  abnSchema,
  AUSIndustrySchema,
  CompanyAddressSchema,
  EntityTypeSchema,
  BusinessOrCompanyAddressSchema,
} from "shared/helpers/Schema";
import Utils from "shared/helpers/Utils";
import { ProfileActions } from "state/profile/Action";
import dayjs from "dayjs";

export const handleSaveBusinessRND = (
  data,
  loanId,
  callback,
  formik = null,
) => {
  LoanService.saveRNDBusiness(
    {
      entityType: data.entityType,
      abn: data.abn.toString(),
      companyAddress: data.companyAddress,
      isABNVerified: true,
      registeredEntityName: data.registeredEntityName,
      entityAddress: data.entityAddress,
      isReviewConfirmed: true,
      lastRegistrationDate: data.auRegDate,
    },
    loanId,
  )
    .then((response: any) => {
      callback(response);
      formik?.setSubmitting(false);
    })
    .catch((error: any) => {
      if (error.response?.data?.msg?.includes("already")) {
        callback(false);
      }
      Utils.handleErrorResponse(error);
      formik?.setSubmitting(false);
    })
    .finally(() => {});
};

export const handleCheckABN = (ev, callback) => {
  LoanService.checkABN({
    abn: ev.abn.toString(),
  })
    .then((response: any) => {
      let data = response.data.data;
      console.log(data);
      callback(data);
    })
    .catch((error: any) => {
      callback(false);
      Utils.handleErrorResponse(error);
    })
    .finally(() => {});
};

const BusinessDetails = () => {
  const {
    handleCompleteAndNext,
    navigateToSectionStepHandler,
    currentProfile,
    isBroker,
  } = useProgress();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { step: urlStep } = useParams<{ step: string }>();
  const theme = useTheme();
  const authInfo = useSelector((state: any) => state.authState);

  const currentStep = urlStep ? parseInt(urlStep, 10) : 1;

  const PAGE_LENGTH = 5;
  const PAGE_ID = "final-business";

  const isValidStep = currentStep >= 1 && currentStep < PAGE_LENGTH;

  useEffect(() => {
    if (!isValidStep) {
      navigate(`/rnd/${PAGE_ID}/1`, { replace: true });
    }
    console.log(currentStep);
  }, [currentStep, isValidStep, navigate]);

  const handleSubmitForm = (value, formik = null) => {
    console.log(value);
    dispatch(ProfileActions.setRndProfile(value));
    const nextStep = currentStep + 1;
    const jumpStep = currentStep + 2;

    if (nextStep < PAGE_LENGTH) {
      navigateToSectionStepHandler(PAGE_ID, nextStep);
    } else {
      if (currentProfile.isAllowedToEdit) {
        if (!currentProfile.isAllowedToEdit?.bankDetails) {
          handleCompleteAndNext();
          return;
        }
      }

      handleSaveBusinessRND(
        { ...currentProfile, auRegDate: value.auRegDate },
        currentProfile.loadId,
        (data) => {
          if (data) handleCompleteAndNext();
        },
        formik,
      );
    }
  };

  if (!isValidStep) {
    return null;
  }

  return (
    <>
      {currentStep === 1 && (
        <Box>
          <Formik
            enableReinitialize
            validationSchema={abnSchema}
            validateOnMount
            onSubmit={(value, formik) => {
              if (value.abn !== currentProfile.abn) {
                formik.setSubmitting(true);
                handleCheckABN(value, (data) => {
                  if (data) {
                    dispatch(
                      ProfileActions.setRndProfile({
                        ...data,
                        entityAddress: `${data.postcode} ${data.state}`,
                        abn: value.abn,
                      }),
                    );
                    formik.setSubmitting(false);
                  } else {
                    formik.setSubmitting(false);
                  }
                });
                return;
              }
              handleSubmitForm(value, formik);
            }}
            initialValues={{
              abn: currentProfile.abn,
            }}
          >
            {({ handleSubmit, errors, isSubmitting }) => (
              <form autoComplete="off">
                <Typography variant="h2">
                  {isBroker ? "Business details" : "Your business"}
                </Typography>
                <Typography variant="h3" mt={3}>
                  {isBroker
                    ? "What is your customer’s ABN?"
                    : "What is your ABN?"}
                </Typography>
                <AppFormikTextField
                  placeholder="Enter ABN number"
                  label=""
                  name="abn"
                  outerLabel
                  className="ezifin-default-field"
                  removeSpacing
                  returnAsString
                />

                {currentProfile.registeredEntityName && (
                  <Box
                    mt={3}
                    sx={{
                      padding: "12px",
                      border: "1px solid #e7e7e8",
                      borderRadius: "6px",
                      backgroundColor: "#f3f3f5",
                    }}
                  >
                    <Typography variant="h3" fontWeight={400}>
                      Name: {currentProfile.registeredEntityName}
                    </Typography>
                    <Typography variant="h3" fontWeight={400}>
                      Address: {currentProfile.entityAddress}
                    </Typography>
                  </Box>
                )}

                <Box mt={4} sx={{ display: "flex", gap: "12px" }}>
                  <AppPrimaryButton
                    title="Continue"
                    isLoading={isSubmitting}
                    loadingText="Please wait.."
                    onClick={handleSubmit}
                    disabled={Object.keys(errors).length > 0 || isSubmitting}
                  />
                </Box>
              </form>
            )}
          </Formik>
        </Box>
      )}

      {currentStep === 2 && (
        <Box>
          <Formik
            enableReinitialize
            validationSchema={EntityTypeSchema}
            validateOnMount
            onSubmit={(value, formik) => handleSubmitForm(value)}
            initialValues={{
              entityType: currentProfile.entityType,
            }}
          >
            {({ handleSubmit, errors }) => {
              return (
                <form autoComplete="off">
                  <Typography variant="h2">
                    {isBroker ? "Business details" : "Your business"}
                  </Typography>
                  <Typography variant="h3" mt={3}>
                    {isBroker
                      ? "What is their entity type?"
                      : "What is your entity type?"}
                  </Typography>

                  <Box mt={2}>
                    <FormControl sx={{ width: 325 }}>
                      <AppFormikSelect
                        placeholder="Select"
                        name="entityType"
                        items={ENTITY_TYPE}
                        valueKey="value"
                        titleKey="label"
                        outerLabel
                        iconComponent={ExpandMore}
                        className="ezifin-default-field"
                      />
                    </FormControl>
                  </Box>

                  <Box mt={4} sx={{ display: "flex", gap: "12px" }}>
                    <AppPrimaryButton
                      title="Continue"
                      isLoading={false}
                      loadingText="Please wait.."
                      onClick={handleSubmit}
                      disabled={Object.keys(errors).length > 0}
                    />
                  </Box>
                </form>
              );
            }}
          </Formik>
        </Box>
      )}

      {currentStep === 3 && (
        <Box>
          <Formik
            enableReinitialize
            validationSchema={CompanyAddressSchema}
            validateOnMount
            onSubmit={(value, formik) => handleSubmitForm(value)}
            initialValues={{
              companyAddress: currentProfile.companyAddress,
            }}
          >
            {({ handleSubmit, errors }) => (
              <form autoComplete="off">
                <Typography variant="h2">
                  {isBroker ? "Business details" : "Your business"}
                </Typography>
                <Typography variant="h3" mt={3}>
                  {isBroker
                    ? " What is your customer company address?"
                    : "What is your company address?"}
                </Typography>
                <AppFormikTextField
                  placeholder="Company address"
                  label=""
                  name="companyAddress"
                  outerLabel
                  className="ezifin-default-field"
                  allowComma
                />

                <Box mt={4} sx={{ display: "flex", gap: "12px" }}>
                  <AppPrimaryButton
                    title="Continue"
                    isLoading={false}
                    loadingText="Please wait.."
                    onClick={handleSubmit}
                    disabled={Object.keys(errors).length > 0}
                  />
                </Box>
              </form>
            )}
          </Formik>
        </Box>
      )}

      {currentStep === 4 && (
        <Box>
          <Formik
            enableReinitialize
            validationSchema={AUSIndustrySchema}
            validateOnMount
            onSubmit={(value, formik) => {
              handleSubmitForm(value, formik);
            }}
            initialValues={{
              auRegDate: currentProfile.auRegDate,
            }}
          >
            {({ handleSubmit, errors, isSubmitting, setValues, values }) => {
              return (
                <form autoComplete="off">
                  <Typography variant="h2">
                    {isBroker ? "Business details" : "Your business"}
                  </Typography>
                  <Typography variant="h3" mt={3}>
                    {isBroker
                      ? "What is your customer Last AUSIndustry Registration Date"
                      : "What is your Last AUSIndustry Registration Date"}
                  </Typography>
                  <Box
                    mt={3}
                    sx={{
                      border: "1px solid #e7e7e7",
                      borderRadius: "6px",
                    }}
                  >
                    <DateCalendar
                      disabled={
                        currentProfile.isAllowedToEdit?.bankDetails === null
                          ? false
                          : currentProfile.isAllowedToEdit?.bankDetails ===
                              undefined
                            ? false
                            : !currentProfile.isAllowedToEdit?.bankDetails
                      }
                      maxDate={dayjs()}
                      onChange={(date) => {
                        const formattedDate = dayjs(date).format("YYYY-MM-DD");
                        setValues({
                          auRegDate: formattedDate,
                        });
                      }}
                      slotProps={{
                        day: {
                          sx: {
                            "&.MuiPickersDay-root.Mui-selected": {
                              backgroundColor: "#2d68f7",
                              color: "white",
                            },
                          },
                        },
                      }}
                      sx={{
                        "&.MuiDateCalendar-root": {
                          maxWidth: "100%",
                        },
                      }}
                      {...(currentProfile.auRegDate
                        ? { value: dayjs(values.auRegDate) }
                        : {})}
                    />
                  </Box>

                  <Box mt={4} sx={{ display: "flex", gap: "12px" }}>
                    <AppPrimaryButton
                      title="Continue"
                      loadingText="Please wait.."
                      onClick={handleSubmit}
                      isLoading={isSubmitting}
                      disabled={Object.keys(errors).length > 0}
                    />
                  </Box>
                </form>
              );
            }}
          </Formik>
        </Box>
      )}
    </>
  );
};

export default BusinessDetails;
